import React, { useEffect, useState } from "react";
import { DataGrid, useGridApiContext } from "@mui/x-data-grid";
import { ReactComponent as Sort } from "../../../../../assets/img/svg/sort.svg";
import { ReactComponent as SortDesc } from "../../../../../assets/img/svg/sortDesc.svg";
import { Select, MenuItem, Box, Typography, IconButton } from "@mui/material";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import "./a-datagrid-style.scss";
import { getUniqueKey } from "../utils/methods/commonMethods/utilityMethod";

// Custom pagination component
export const CustomPagination = ({
  rowCount,
  onPageChange,
  onPageSizeChange,
  pageSize,
  page,
  rowsPerPageOptions = [10, 20, 30, 40, 50, 100, 200],
}) => {
  const apiRef = useGridApiContext();

  const pageCount = Math.ceil(rowCount / pageSize);

  // !issue: apiRef->pageSize is still the default 100 from MUI on initial render
  // const {
  //   state: {
  //     pagination: { pageCount },
  //   },
  // } = apiRef.current;

  const from = rowCount ? page * pageSize + 1 : 0;
  const to = Math.min((page + 1) * pageSize, rowCount);

  const goPage = (newPage) => {
    if (onPageChange) onPageChange(newPage);
    apiRef.current.setPage(newPage);
  };

  const changePageSize = (newSize) => {
    if (onPageSizeChange) onPageSizeChange(newSize);
    apiRef.current.setPageSize(newSize);
    // Reset to first page when page size changes to keep server requests consistent
    apiRef.current.setPage(0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        backgroundColor: "#F7F8FA",
        borderTop: "1px solid #E3E8EF",
        p: "0.5rem 1rem",
        fontSize: "0.875rem",
      }}
    >
      {/* Left: rows-per-page + count */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Select
          size="small"
          value={pageSize}
          onChange={(e) => changePageSize(e.target.value)}
          sx={{
            width: 72,
            backgroundColor: "#E8EAF0",
            borderRadius: "8px",
            ".MuiSelect-select": { padding: "6px 8px", textAlign: "center" },
            ".MuiOutlinedInput-notchedOutline": { border: "none" },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                "& .MuiMenu-list": {
                  display: "flex",
                  flexDirection: "column", // force vertical stacking
                  paddingTop: "8px",
                  paddingBottom: "8px",
                },
              },
            },
          }}
        >
          {rowsPerPageOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
        <Typography sx={{ color: "#666666" }}>Items per page</Typography>
        <Typography
          sx={{ color: "#666666" }}
        >{`${from}-${to} of ${rowCount} items`}</Typography>
      </Box>
      {/* Right: page dropdown + of pages + prev/next */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Select
          size="small"
          value={page + 1}
          onChange={(e) => goPage(e.target.value - 1)}
          sx={{
            width: 72,
            backgroundColor: "#E8EAF0",
            borderRadius: "8px",
            ".MuiSelect-select": { padding: "6px 8px", textAlign: "center" },
            ".MuiOutlinedInput-notchedOutline": { border: "none" },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                "& .MuiMenu-list": {
                  display: "flex",
                  flexDirection: "column", //  force vertical stacking
                  paddingTop: "8px", // restore MUI default
                  paddingTop: "8px", // restore MUI default
                  paddingBottom: "8px",
                },
              },
            },
          }}
        >
          {Array.from({ length: pageCount }).map((_, i) => (
            <MenuItem key={i} value={i + 1}>
              {i + 1}
            </MenuItem>
          ))}
        </Select>
        <Typography
          sx={{ color: "#666666" }}
        >{`of ${pageCount} pages`}</Typography>
        <IconButton
          size="small"
          onClick={() => goPage(page - 1)}
          disabled={page === 0}
        >
          <FaChevronLeft />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => goPage(page + 1)}
          disabled={page + 1 >= pageCount}
        >
          <FaChevronRight />
        </IconButton>
      </Box>
    </Box>
  );
};

const EnhanceDataGrid = (props) => {
  const DEFAULT_PAGE_SIZE = 50;
  const [sortModel, setSortModel] = useState([]);
  const [uniqueClassName] = useState(`class-${getUniqueKey()}`);

  const {
    rows,
    columns,
    rowId,
    checkboxSelection,
    columnFilter,
    columnMenu,
    loading,
    onRowClick,
    rowCount,
    nextButtonHandler = () => {},
    height,
    emptyRowPlaceholder,
    sortColumnName,
    serverPagination = true,
    rowHeight = 50,
    sx = {},
    editMode = "row",
    paginationModel = { page: 0, pageSize: DEFAULT_PAGE_SIZE },
    onPaginationModelChange,
    removeWrapperContainer = false,
    hidePagination = false,
    pinLastColumn = false,
    maxHeight,
    checkbox = [],
    noRowsOverlayMessage = "No Rows",
    ...restProps
  } = props;
  useEffect(() => {
    if (sortColumnName) setSortModel([{ field: sortColumnName, sort: "desc" }]);
  }, [sortColumnName]);

  useEffect(() => {
    if (!pinLastColumn) return;
    const findVs = () => {
      const vs = document.querySelector(
        `.${uniqueClassName} .MuiDataGrid-virtualScroller`
      );
      if (!vs) return setTimeout(findVs, 100);
      vs.addEventListener("scroll", handleScrollHorizontal);
      return () => vs.removeEventListener("scroll", handleScrollHorizontal);
    };
    findVs();
  }, [pinLastColumn]);

  const handleScrollHorizontal = () => {
    const scrolled = document.querySelector(
      `.${uniqueClassName} .MuiDataGrid-virtualScroller`
    ).scrollLeft;
    const headers = document.querySelectorAll(
      `.${uniqueClassName} .MuiDataGrid-columnHeader:not(.pin-last-column):not(.pin-right-1):not(.pin-right-2)`
    );
    const headerInner = document.querySelector(
      `.${uniqueClassName} .MuiDataGrid-columnHeadersInner`
    );
    headerInner.style.transform = "none";
    headers.forEach(
      (h) => (h.style.transform = `translate3d(-${scrolled}px,0,0)`)
    );
  };

  const getDataGrid = () => {
    const enhancedColumns = pinLastColumn
      ? columns.map((col, idx) =>
          idx === columns.length - 1
            ? {
                ...col,
                headerClassName: "pin-last-column",
                cellClassName: "pin-last-column",
              }
            : col
        )
      : columns;

    return (
      <DataGrid
        className={`${uniqueClassName} ${restProps.className || ""}`}
        rows={rows}
        columns={enhancedColumns}
        loading={loading}
        pagination
        paginationMode={serverPagination ? "server" : "client"}
        rowCount={rowCount}
        rowsPerPageOptions={[10, 20, 30, 40, 50, 100, 200]}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => {
          if (onPaginationModelChange) onPaginationModelChange(model);
          nextButtonHandler(model.page, model.pageSize);
        }}
        components={{
          Pagination: CustomPagination,
          ColumnSortedDescendingIcon: SortDesc,
          ColumnSortedAscendingIcon: Sort,
          NoRowsOverlay:
            emptyRowPlaceholder ||
            (() => (
              <div className="d-flex justify-content-center align-items-center h-100">
                {noRowsOverlayMessage}
              </div>
            )),
        }}
        componentsProps={{
          pagination: {
            pageSize: paginationModel?.pageSize,
            page: paginationModel?.page,
            rowCount,
            onPageChange: (newPage) =>
              nextButtonHandler(newPage, paginationModel.pageSize),
            // When page size changes, always request page 0 on the server
            onPageSizeChange: (newSize) => nextButtonHandler(0, newSize),
          },
          ...checkbox,
        }}
        getRowId={rowId}
        checkboxSelection={!!checkboxSelection}
        isRowSelectable={() => !!checkboxSelection}
        density="compact"
        disableColumnFilter={!columnFilter}
        disableColumnMenu={!columnMenu}
        getRowHeight={() => rowHeight}
        onRowClick={(params, event) => {
          event.stopPropagation();
          if (onRowClick) onRowClick(params.row);
        }}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        sx={{
          color: "#262626",
          fontFamily: "inherit",
          ".MuiDataGrid-columnHeader": { fontFamily: "Gilroy-Bold" },
          ".MuiDataGrid-cell:focus, .MuiDataGrid-columnHeader:focus": {
            outline: "none",
          },
          ".MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus-within":
            { outline: "none" },
          ".MuiDataGrid-cell--editing:focus-within, .MuiDataGrid-cell--editing:focus, .MuiDataGrid-cell--editing:focus-visible":
            {
              outline: "none !important",
            },
          ".MuiDataGrid-row--editing": {
            boxShadow: "none",
          },
          ".MuiDataGrid-footerContainer": {
            display: hidePagination ? "none" : undefined,
          },
          ".MuiDataGrid-main": {
            height: props.autoHeight ? "unset" : undefined,
          },
          ".MuiDataGrid-selectedRowCount": {
            display: "none",
          },
          /* ⭐ CUSTOM CHECKBOX STYLE — 18.5px + border + correct padding ⭐ */
          "& .MuiDataGrid-cellCheckbox .MuiCheckbox-root": {
            padding: "2.75px !important", // your exact padding
          },

          "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
            padding: "2.75px !important",
          },

          /* Checkbox size */
          "& .MuiCheckbox-root .MuiSvgIcon-root": {
            width: "18.5px !important",
            height: "18.5px !important",
          },

          /* Unchecked checkbox box border + background */
          "& .MuiDataGrid-cellCheckbox .MuiCheckbox-root .MuiSvgIcon-root": {
            borderRadius: "4px !important",
            backgroundColor: "#FFFFFF !important",
            border: "1.5px solid #E2E2E2 !important",
            fill: "none !important",
            stroke: "none !important",
          },

          "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root .MuiSvgIcon-root":
            {
              borderRadius: "4px !important",
              backgroundColor: "#FFFFFF !important",
              border: "1.5px solid #E2E2E2 !important",
              fill: "none !important",
              stroke: "none !important",
            },

          /* Checked state (keeps your theme color) */
          "& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root": {
            backgroundColor: "#1976D2 !important", // your checked fill
            border: "1.5px solid #1976D2 !important",
            fill: "#FFFFFF !important",
            stroke: "transparent !important",
          },
          /* Disabled checkbox style */
          "& .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root": {
            backgroundColor: "#F2F2F2 !important", // light grey background
            border: "1.5px solid #DADADA !important",
            opacity: "1 !important", // prevent MUI dim opacity
          },

          /* Disabled + checked */
          "& .MuiCheckbox-root.Mui-disabled.Mui-checked .MuiSvgIcon-root": {
            backgroundColor: "#BDBDBD !important",
            border: "1.5px solid #BDBDBD !important",
            fill: "#FFFFFF !important",
          },

          /* Hover effect (optional but recommended) */
          "& .MuiDataGrid-cellCheckbox:hover .MuiCheckbox-root .MuiSvgIcon-root":
            {
              backgroundColor: "#F8F8F8 !important",
            },

          ...(maxHeight
            ? {
                ".MuiDataGrid-main": {
                  maxHeight,
                },
              }
            : {}),
          ...(pinLastColumn
            ? {
                "& .MuiDataGrid-columnHeadersInner": {
                  transform: "none !important",
                },
                "& .MuiDataGrid-columnHeader.pin-last-column": {
                  position: "sticky",
                  right: 0,
                  backgroundColor: "#FFF",
                  zIndex: 1,
                },
                "& .MuiDataGrid-cell.pin-last-column": {
                  position: "sticky",
                  right: 0,
                  backgroundColor: "#FFF",
                  zIndex: 1,
                  borderLeft: "1.2px solid #E2E2E2",
                },
                "& .MuiDataGrid-cell.pin-right-2": {
                  backgroundColor: "#FFF",
                  borderLeft: "1.2px solid #E2E2E2",
                },
                "& .MuiDataGrid-row:not(.MuiDataGrid-row--editing):hover .MuiDataGrid-cell.pin-last-column,& .MuiDataGrid-row:not(.MuiDataGrid-row--editing):hover .MuiDataGrid-cell.pin-right-2":
                  {
                    backgroundColor: "#F5F5F5 !important",
                  },
              }
            : {}),
          ...sx,
        }}
        editMode={editMode}
        experimentalFeatures={{ newEditingApi: true }}
        onRowEditStart={(params, e) => (e.defaultMuiPrevented = true)}
        onRowEditStop={(params, e) => (e.defaultMuiPrevented = true)}
        onCellFocusOut={(params, e) => (e.defaultMuiPrevented = true)}
        onRowDoubleClick={(params, e) => (e.defaultMuiPrevented = true)}
        onCellDoubleClick={(params, e) => (e.defaultMuiPrevented = true)}
        {...restProps}
      />
    );
  };

  const wrapperStyle = {
    minHeight: height || 370,
    flexGrow: 1,
    display: "flex",
    flexFlow: "column",
  };
  return removeWrapperContainer ? (
    getDataGrid()
  ) : (
    <div className="mt-3" style={wrapperStyle}>
      {getDataGrid()}
    </div>
  );
};

export default EnhanceDataGrid;
